import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import 'leaflet-routing-machine';

import storeIcon1 from '../../assets/Markers1.svg';
import storeIcon2 from '../../assets/Markers2.svg';
import storeIcon3 from '../../assets/Markers3.svg';
import storeIcon4 from '../../assets/Markers4.svg';
import currentLocationIcon from '../../assets/location.svg';
import { Badge } from '@/components/ui/badge';
import { MapPin } from 'lucide-react';
import { formatNumberWithDotAndComma } from '@/utils/moneyFormatter';

interface MarkerInfo {
  id: number;
  position: L.LatLngExpression;
  title: string;
  owner: string;
  icon: L.Icon<L.IconOptions>;
}

interface MapCompoentProps {
  coordinates: CollectionOrder[];
}

const MapComponent: React.FC<MapCompoentProps> = ({ coordinates }) => {
  const algeriaCoordinates: L.LatLngExpression = [36.7538, 3.0588]; // Algiers coordinates
  const [currentPosition, setCurrentPosition] =
    useState<L.LatLngExpression | null>(null);
  const [mapInstance, setMapInstance] = useState<L.Map | null>(null);

  const customIcons = {
    'Awaiting Collection': L.icon({
      iconUrl: storeIcon1,
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      shadowSize: [41, 41],
    }),
    'Payment Collected': L.icon({
      iconUrl: storeIcon2,
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      shadowSize: [41, 41],
    }),
    'Payment Deposited': L.icon({
      iconUrl: storeIcon3,
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      shadowSize: [41, 41],
    }),
    Blocked: L.icon({
      iconUrl: storeIcon4,
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      shadowSize: [41, 41],
    }),
    currentLocation: L.icon({
      iconUrl: currentLocationIcon,
      iconSize: [30, 45],
      iconAnchor: [15, 0],
      popupAnchor: [0, -20],
    }),
  };

  const [selectedMarkers, setSelectedMarkers] = useState<L.LatLngExpression[]>(
    []
  );

  const handleMarkerClick = (marker: CollectionOrder) => {
    setSelectedMarkers((prev: any) => {
      if (prev.length < 2) {
        return [...prev, marker.location];
      } else {
        return [marker.location];
      }
    });
  };

  const parseLocation = (location: string): L.LatLngExpression => {
    if (!location || !location.includes(',')) {
      return [36.18236315392393, 1.3874258580935497]; // Provide default coordinates or handle invalid input properly
    }
    const [lat, lng] = location.split(',').map(Number);
    if (isNaN(lat) || isNaN(lng)) {
      return [36.18236315392393, 1.3874258580935497]; // Fallback for invalid lat/lng values
    }
    return [lat, lng];
  };

  useEffect(() => {
    if (!mapInstance) {
      return;
    }

    const validMarkers = coordinates
      .map((marker: CollectionOrder) => {
        return parseLocation(marker.location);
      })
      .filter((pos: any) => pos[0] !== 0 && pos[1] !== 0); // Filter out invalid coordinates

    let bounds = L.latLngBounds(validMarkers);

    if (currentPosition) {
      bounds.extend(currentPosition as [number, number]);
    }

    if (bounds.isValid() && validMarkers.length > 0) {
      mapInstance.fitBounds(bounds);
    } else {
      console.warn('No valid bounds to fit');
    }
  }, [mapInstance, currentPosition, coordinates]);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position) => {
        setCurrentPosition([
          position.coords.latitude,
          position.coords.longitude,
        ]);
      });
    }
  }, []);

  return (
    <MapContainer
      center={algeriaCoordinates}
      zoom={12}
      className='w-full h-[95%] mt-2 rounded-xl z-10'
      ref={setMapInstance}
    >
      <TileLayer url='https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png' />
      {coordinates.map((marker) => {
        const markerPosition = parseLocation(marker.location); // Parse location string
        const dateValue = new Date(marker.date);
        const today = new Date();

        // Calculate the difference in days between the date and today
        const oneDayInMs = 24 * 60 * 60 * 1000;
        const differenceInDays = Math.ceil(
          (dateValue.getTime() - today.getTime()) / oneDayInMs
        );

        const formattedDate = dateValue.toLocaleDateString('en-US', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        });

        let color = 'inherit';
        if (differenceInDays === 0) {
          color = 'red'; // due today
        } else if (differenceInDays === 1) {
          color = '#F7C74E'; // due tomorrow
        }

        const coordinates = marker.location; // Access location field directly from row.original
        const googleMapsUrl = `https://www.google.com/maps?q=${coordinates}`;

        return (
          <Marker
            key={marker.id}
            position={markerPosition}
            icon={
              marker.status === null || marker.status === undefined
                ? customIcons.currentLocation
                : customIcons[marker.status]
            }
            eventHandlers={{
              click: () => handleMarkerClick(marker),
            }}
          >
            <Popup className=''>
              <div className='flex -my-6 items-center gap-2 '>
                <p className='m-0  text-base font-bold'>
                  {marker?.Collector?.fullname}
                </p>
                <Badge
                  variant={
                    marker.status !== undefined || marker.status !== null
                      ? marker.status
                      : 'default'
                  }
                  className='text-[8px] font-semibold'
                >
                  {marker.status}
                </Badge>
              </div>

              <div className='flex items-center'>
                <div className='w-[80%]  '>
                  <p className='text-muted-foreground '>
                    Client:{' '}
                    <span className='text-sm font-bold text-purple-500'>
                      {marker.Customer?.commercial_name}
                    </span>
                  </p>
                  <p className='text-muted-foreground '>
                    Montant:{' '}
                    <span className='text-sm font-bold text-purple-500'>
                      {formatNumberWithDotAndComma(marker.amount.toString())}
                    </span>
                  </p>
                  <p className='text-black'>
                    <span className='text-muted-foreground '>Date: </span>
                    <span className='text-sm font-bold' style={{ color }}>
                      {formattedDate}
                    </span>
                  </p>
                </div>

                <div className=' w-[20%] flex justify-center items-center '>
                  <a
                    className='bg-muted  h-[40px] w-[40px] rounded-full flex justify-center items-center hover:bg-primary/10 transition ease-in-out duration-200'
                    href={googleMapsUrl}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    <MapPin size={20} className='inline' />
                  </a>
                </div>
              </div>
            </Popup>
          </Marker>
        );
      })}
      {currentPosition && (
        <Marker position={currentPosition} icon={customIcons.currentLocation}>
          <Popup>You are here</Popup>
        </Marker>
      )}
    </MapContainer>
  );
};

export default MapComponent;
